import {
  BackendError,
  ConflictError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  UnauthorizedError,
  UnprocessableEntityError,
} from "@exceptions";

export interface MutationDefaultOptions {
  errorKey: string;
}

export interface PaginationInfo {
  total: number;
  count: number;
  per_page: number;
  current_page: number;
  total_pages: number;
  links: [];
}

export type FilterableFields = {
  [key: string]: "text" | "boolean";
};

export interface BackendResponseFormat<T> {
  data?: T;
  message?: string;
  errors?: { [key: string]: string[] };
  meta?: {
    pagination?: PaginationInfo;
    sortable_fields?: string[];
    filterable_fields?: FilterableFields;
    error_code?: number;
    matched_rows?: number;
    product_code?: string;
    missing_product_codes?: string[];
  };
}

interface FetchOptions {
  url: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: HeadersInit;
  body?: unknown;
  language?: string;
  multipart?: boolean;
  accept?: string;
}

/**
 * Converts form data to FormData if it contains files, otherwise returns the original data
 * @param data - The form data to potentially convert
 * @returns Object with the processed body and whether it should use multipart
 */
export function prepareRequestBody<T extends object>(
  data: T,
): {
  body: FormData | T;
  multipart: boolean;
} {
  // Check if data contains files for multipart submission
  const hasFiles = Object.values(data as Record<string, unknown>).some(
    (value) => value instanceof FileList || value instanceof File,
  );

  if (!hasFiles) {
    return { body: data, multipart: false };
  }

  // Convert to FormData for file uploads
  const formData = new FormData();

  /**
   * Recursively append nested objects and arrays to FormData
   * @param obj - The object to append
   * @param prefix - The field name prefix for nested keys
   */
  const appendToFormData = (
    obj: Record<string, unknown>,
    prefix = "",
  ): void => {
    Object.entries(obj).forEach(([key, value]) => {
      const fieldName = prefix ? `${prefix}[${key}]` : key;

      if (value instanceof FileList && value.length > 0) {
        // Handle FileList (from file input)
        for (let i = 0; i < value.length; i++) {
          formData.append(fieldName, value[i]);
        }
      } else if (value instanceof File) {
        // Handle single File
        if (value.size > 50 * 1024 * 1024) {
          throw new Error(`File ${value.name} exceeds maximum size limit`);
        }
        formData.append(fieldName, value);
      } else if (Array.isArray(value)) {
        // Handle arrays by iterating with index
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            appendToFormData(
              item as Record<string, unknown>,
              `${fieldName}[${index}]`,
            );
          } else if (item !== null && item !== undefined) {
            formData.append(`${fieldName}[${index}]`, String(item));
          }
        });
      } else if (typeof value === "object" && value !== null) {
        // Handle nested objects
        appendToFormData(value as Record<string, unknown>, fieldName);
      } else if (value !== null && value !== undefined) {
        // Handle primitive values
        formData.append(fieldName, String(value));
      } else if (value === null) {
        // Handle null values explicitly for file deletion
        formData.append(fieldName, "");
      }
    });
  };

  appendToFormData(data as Record<string, unknown>);

  return { body: formData, multipart: true };
}

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
}

export async function backendFetch({
  url,
  method = "GET",
  headers = {},
  body,
  language = "en-US",
  multipart = false,
}: FetchOptions) {
  const xsrfToken = getCookie("XSRF-TOKEN");
  const defaultHeaders: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": language,
  };

  if (!multipart) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  if (xsrfToken) {
    defaultHeaders["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);
  }

  let requestBody: BodyInit | undefined;

  if (body) {
    if (multipart) {
      requestBody = body as FormData;
    } else {
      requestBody = JSON.stringify(body);
    }
  } else {
    requestBody = undefined;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
      body: requestBody,
      credentials: "include",
    });
  } catch (error) {
    throw new NetworkError(error);
  }

  const contentType = response.headers.get("content-type");

  let responseBody: BackendResponseFormat<unknown> | string | Blob;
  if (contentType?.includes("application/json")) {
    responseBody = await response.json();
  } else if (contentType?.includes("application/pdf")) {
    responseBody = await response.blob();
  } else {
    responseBody = await response.text();
  }

  if (!response.ok) {
    const statusCode = response.status;

    if (responseBody instanceof Blob) {
      responseBody = await responseBody.text();
    }

    throw errorForStatus(statusCode, responseBody);
  }

  return responseBody;
}

function errorForStatus(
  status: number,
  body: string | BackendResponseFormat<unknown>,
): Error {
  switch (status) {
    case 401:
      return new UnauthorizedError(body);
    case 403:
      return new ForbiddenError(body);
    case 404:
      return new NotFoundError(body);
    case 409:
      return new ConflictError(body);
    case 422:
      return new UnprocessableEntityError(body);
    default:
      return new BackendError(status, body);
  }
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

interface UploadFileOptions {
  url: string;
  file: File;
  fieldName?: string;
  fields?: Record<string, string>;
  headers?: Record<string, string>;
  language?: string;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

/**
 * Upload a single file via XMLHttpRequest so request-body progress can be
 * reported — fetch cannot surface upload progress. Mirrors backendFetch's auth
 * (XSRF cookie, credentials) and maps HTTP status to the same typed exceptions.
 */
export function uploadFileWithProgress<T = unknown>({
  url,
  file,
  fieldName = "file",
  fields = {},
  headers = {},
  language = "en-US",
  onProgress,
  signal,
}: UploadFileOptions): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const formData = new FormData();
    formData.append(fieldName, file);
    Object.entries(fields).forEach(([key, value]) =>
      formData.append(key, value),
    );

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Accept-Language", language);

    const xsrfToken = getCookie("XSRF-TOKEN");
    if (xsrfToken) {
      xhr.setRequestHeader("X-XSRF-TOKEN", decodeURIComponent(xsrfToken));
    }
    Object.entries(headers).forEach(([key, value]) =>
      xhr.setRequestHeader(key, value),
    );

    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.round((event.loaded / event.total) * 100),
        });
      }
    };

    xhr.onload = () => {
      const contentType = xhr.getResponseHeader("content-type") ?? "";
      const body =
        contentType.includes("application/json") && xhr.responseText
          ? JSON.parse(xhr.responseText)
          : xhr.responseText;

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
        return;
      }

      reject(errorForStatus(xhr.status, body));
    };

    xhr.onerror = () => reject(new NetworkError(new Error("Upload failed")));
    xhr.onabort = () =>
      reject(new DOMException("Upload aborted", "AbortError"));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.send(formData);
  });
}
